"use client";

import { useState } from "react";
import Image from "next/image";
import ProductImageLightbox from "./ProductImageLightbox";

interface ProductImageGalleryProps {
	images: string[];
	productName: string;
	hasDiscount?: boolean;
	price?: number | null;
	discountedPrice?: number | null;
}

const ProductImageGallery = ({
	images,
	productName,
	hasDiscount = false,
	price,
	discountedPrice,
}: ProductImageGalleryProps) => {
	const [selectedImage, setSelectedImage] = useState(0);
	const [lightboxOpen, setLightboxOpen] = useState(false);

	// Calculate discount percentage
	const discountPercentage =
		hasDiscount && price && discountedPrice
			? Math.round(((price - discountedPrice) / price) * 100)
			: 0;

	// If there's only one image, display it in a larger format
	if (images.length === 1) {
		return (
			<div className="flex flex-col gap-4">
				<div
					className="relative aspect-square w-full overflow-hidden rounded-lg cursor-zoom-in"
					onClick={() => setLightboxOpen(true)}
				>
					<Image
						src={images[0]}
						alt={`${productName}`}
						fill
						className="object-cover"
						priority
						quality={85}
						sizes="(max-width: 768px) 100vw, 50vw"
						placeholder="blur"
						blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlMmUyZTIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9InN5c3RlbS11aSI7IGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5YTlhOWEiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg=="
					/>
					{hasDiscount && discountPercentage > 0 && (
						<div className="absolute top-2 right-2 bg-red-500 text-white font-bold px-3 py-1 rounded-full shadow-lg">
							{discountPercentage}% Discount
						</div>
					)}
				</div>

				{/* Lightbox for single image */}
				<ProductImageLightbox
					isOpen={lightboxOpen}
					onClose={() => setLightboxOpen(false)}
					images={images}
					currentIndex={0}
					productName={productName}
				/>
			</div>
		);
	}

	// Navigate to previous image
	const prevImage = (e?: React.MouseEvent) => {
		if (e) e.stopPropagation();
		setSelectedImage((prev) => (prev === 0 ? images.length - 1 : prev - 1));
	};

	// Navigate to next image
	const nextImage = (e?: React.MouseEvent) => {
		if (e) e.stopPropagation();
		setSelectedImage((prev) => (prev === images.length - 1 ? 0 : prev + 1));
	};

	// Open lightbox
	const openLightbox = () => {
		setLightboxOpen(true);
	};

	// Close lightbox
	const closeLightbox = () => {
		setLightboxOpen(false);
	};

	return (
		<div className="flex flex-col gap-4">
			{/* Main Image */}
			<div
				className="relative aspect-square w-full overflow-hidden rounded-lg cursor-zoom-in"
				onClick={openLightbox}
			>
				<Image
					src={images[selectedImage]}
					alt={`${productName} - Image ${selectedImage + 1}`}
					fill
					className="object-cover"
					priority
					quality={85}
					sizes="(max-width: 768px) 100vw, 50vw"
					placeholder="blur"
					blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlMmUyZTIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9InN5c3RlbS11aSI7IGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5YTlhOWEiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg=="
				/>
				{hasDiscount && discountPercentage > 0 && (
					<div className="absolute top-2 right-2 bg-red-500 text-white font-bold px-3 py-1 rounded-full shadow-lg">
						{discountPercentage}% Discount
					</div>
				)}

				{/* Navigation arrows */}
				{images.length > 1 && (
					<>
						<button
							onClick={(e) => {
								e.stopPropagation();
								prevImage();
							}}
							className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
							aria-label="Previous image"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={2}
								stroke="currentColor"
								className="w-6 h-6"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15.75 19.5L8.25 12l7.5-7.5"
								/>
							</svg>
						</button>
						<button
							onClick={(e) => {
								e.stopPropagation();
								nextImage();
							}}
							className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
							aria-label="Next image"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={2}
								stroke="currentColor"
								className="w-6 h-6"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M8.25 4.5l7.5 7.5-7.5 7.5"
								/>
							</svg>
						</button>
					</>
				)}

				{/* Image counter */}
				{images.length > 1 && (
					<div className="absolute bottom-4 right-4 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
						{selectedImage + 1} / {images.length}
					</div>
				)}
			</div>

			{/* Thumbnails */}
			{images.length > 1 && (
				<div className="flex gap-2 overflow-x-auto p-2">
					{images.map((image, index) => (
						<button
							key={index}
							onClick={() => setSelectedImage(index)}
							className={`relative aspect-square w-20 flex-shrink-0 rounded-md overflow-hidden ${
								selectedImage === index
									? "ring-2 ring-primary"
									: "ring-1 ring-gray-200"
							}`}
						>
							<Image
								src={image}
								alt={`${productName} - Thumbnail ${index + 1}`}
								fill
								className="object-cover"
								sizes="80px"
								loading={index === 0 ? "eager" : "lazy"}
								quality={75}
								placeholder="blur"
								blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlMmUyZTIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9InN5c3RlbS11aSI7IGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5YTlhOWEiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg=="
							/>
						</button>
					))}
				</div>
			)}

			{/* Lightbox component */}
			<ProductImageLightbox
				isOpen={lightboxOpen}
				onClose={closeLightbox}
				images={images}
				currentIndex={selectedImage}
				productName={productName}
			/>
		</div>
	);
};

export default ProductImageGallery;
